import { getData, postData, patchData } from './FetchNodeServices';
import type { MockJobWorkOrder } from '@/mocks/billing';

export interface CreateJobWorkPayload {
  date: string;
  subContractorId: string;
  subContractorName?: string;
  subContractorGSTIN?: string;
  warehouseId: string;
  jobDescription: string;
  jobCharges: number;
  expectedReturnDate: string;
  productionOrderId?: string | null;
  notes?: string | null;
  items: Array<{
    itemId: string;
    itemName: string;
    hsnCode?: string;
    sentQty: number;
    unit: string;
    rate: number;
  }>;
}

export interface ITC04ReportResponse {
  startDate: string;
  endDate: string;
  sentData: Array<{
    slNo: number;
    challanNo: string;
    date: string;
    subContractorGSTIN: string;
    itemDescription: string;
    hsn: string;
    qty: number;
    unit: string;
    value: number;
  }>;
  receivedData: Array<{
    slNo: number;
    jwoRef: string;
    receiptDate: string;
    item: string;
    hsn: string;
    qtyReceived: number;
    qtyRejected: number;
    value: number;
  }>;
}

export const jobWorkService = {
  // Fetch all Job Work Orders with optional filters
  getAll: async (params?: { status?: string; warehouseId?: string; subContractorId?: string; search?: string }): Promise<MockJobWorkOrder[]> => {
    try {
      const res = await getData('api/v1/manufacturing/jobwork', params);
      if (res && res.success && Array.isArray(res.data)) {
        return res.data;
      }
      return Array.isArray(res) ? res : [];
    } catch (err) {
      console.error('jobWorkService.getAll error:', err);
      return [];
    }
  },

  // Fetch single Job Work Order details by ID
  getById: async (id: string): Promise<MockJobWorkOrder | null> => {
    try {
      const res = await getData(`api/v1/manufacturing/jobwork/${id}`);
      if (res && res.success && res.data) {
        return res.data;
      }
      return null;
    } catch (err) {
      console.error('jobWorkService.getById error:', err);
      return null;
    }
  },

  // Create new Job Work Order
  create: async (payload: CreateJobWorkPayload) => {
    return await postData('api/v1/manufacturing/jobwork', payload);
  },

  // Issue Delivery Challan
  issueChallan: async (id: string) => {
    return await patchData(`api/v1/manufacturing/jobwork/${id}/issue`, {});
  },

  // Record material receipt & rejections
  recordReceipt: async (id: string, receiptItems: Record<string, { receivedQty: number; rejectedQty: number; notes?: string }>) => {
    return await patchData(`api/v1/manufacturing/jobwork/${id}/receipt`, { receiptItems });
  },

  // Cancel Job Work Order
  cancel: async (id: string) => {
    return await patchData(`api/v1/manufacturing/jobwork/${id}/cancel`, {});
  },

  // Get dedicated Sub-Contractors
  getSubContractors: async (params?: { warehouseId?: string }) => {
    try {
      let url = 'api/v1/manufacturing/jobwork/subcontractors';
      if (params?.warehouseId) {
        url += `?warehouseId=${encodeURIComponent(params.warehouseId)}`;
      }
      const res = await getData(url);
      if (res && res.success && Array.isArray(res.data)) {
        return res.data;
      }
      return Array.isArray(res) ? res : [];
    } catch (err) {
      console.error('jobWorkService.getSubContractors error:', err);
      return [];
    }
  },

  // Create new Sub-Contractor
  createSubContractor: async (payload: {
    name: string;
    gstin?: string;
    phone?: string;
    email?: string;
    address?: string;
    city?: string;
    state?: string;
    warehouseId?: string;
  }) => {
    return await postData('api/v1/manufacturing/jobwork/subcontractors', payload);
  },
};
